import React from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import styles from './WhySpecial.module.sass';
import Button from '../../../ui_kit/button/Button';
import Input from '../../../ui_kit/input/Input';
import BackButton from '../back_button/BackButton';
import Accordion from '../../../ui_kit/accordion/Accordion';
import VideoIcon from './VideoIcon';

function WhySpecial(props) {
  const {
    increaseStep, decreaseStep, quiz, step,
  } = props;

  const {
    uniqueIngredient, application, lookAndFeel, shades, videoUrl,
  } = quiz;

  const onNextClick = (data) => {
    increaseStep({ ...quiz, ...data });
  };

  const {
    handleSubmit, formState: { errors }, register,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      uniqueIngredient, application, lookAndFeel, shades, videoUrl,
    },
  });

  return (
    <>
      <BackButton
        decreaseStep={() => {
          decreaseStep({
            ...quiz, uniqueIngredient, application, lookAndFeel, shades, videoUrl,
          });
        }}
      />
      <form className={styles['why-special']} onSubmit={handleSubmit(onNextClick)}>
        <div className={styles.content}>
          {step}
          <h1 className={styles.title}>
            Why is this product special?
          </h1>
          <div className={styles.description}>
            (i.e. unique ingredient, sustainable, clean, vegan?)
          </div>
          <div className={styles.form}>
            <Input
              className={styles.input}
              label="Do you have any unique ingredients in mind?*"
              placeholder="What superstar additions do you want in your product formulation?"
              error={errors.uniqueIngredient?.message}
              {...register('uniqueIngredient', { required: 'This field is required' })}
            />
            <Input
              className={styles.input}
              label="What is special about how this product is applied?"
              placeholder="Think about how the packaging works & how the product is used"
              {...register('application')}
            />
            <Input
              className={styles.input}
              label="What is unique about the look/feel of this product?*"
              placeholder="Think texture, effect, wear, payoff, coverage, matte vs. glossy etc."
              error={errors.lookAndFeel?.message}
              {...register('lookAndFeel', { required: 'This field is required' })}
            />
            <Input
              className={styles.input}
              label="If applicable, how many shades/colors does this product come in?"
              placeholder="Not just number of shades, but the whole color palette too"
              {...register('shades')}
            />
            <div className={styles.subtitle}>
              <VideoIcon />
              Create & upload a video bringing your innovation to life. This is a crucial
              step to show how your personal story weaves into your beauty idea.
            </div>
            <Accordion
              heading="Tips for the perfect submission"
              content={(
                <>
                  <ul>
                    <li>Please film horizontally</li>
                    <li>Smartphone or webcam quality is perfect</li>
                    <li>Keep duration to 1-3 min long</li>
                    <li>
                      Briefly introduce yourself with anything you&apos;d like us to know about you
                    </li>
                    <li>
                      Explain your dream idea:
                      <ul>
                        <li>What&apos;s your inspiration?</li>
                        <li>What are the key benefits?</li>
                        <li>Please provide interesting ingredients</li>
                        <li>Describe the texture you’re envisioning</li>
                        <li>
                          Have you tried similar products in the past?
                          What were they and how is your idea different?
                        </li>
                      </ul>
                    </li>
                    <li>If your idea is a DIY, please show it to us! Texture, application, etc.</li>
                  </ul>
                  <p>
                    *This video is only previewed internally and will
                    not be shared without your consent
                  </p>
                </>
            )}
              className={styles.accordion}
              headingClassName={styles['accordion-header']}
              background="transparent"
            />
            <Input
              className={styles.input}
              placeholder="Paste link (youtube, google drive, dropbox, vimeo, etc)"
              {...register('videoUrl')}
            />
          </div>
          <Button
            className={`${styles.button} custom-transparent-button`}
            type="submit"
            text="NEXT"
            color="transparent-blue"
            size="small"
          />
        </div>
      </form>
    </>
  );
}

WhySpecial.propTypes = {
  increaseStep: PropTypes.func.isRequired,
  decreaseStep: PropTypes.func.isRequired,
  quiz: PropTypes.shape({
    uniqueIngredient: PropTypes.string,
    application: PropTypes.string,
    lookAndFeel: PropTypes.string,
    shades: PropTypes.string,
    videoUrl: PropTypes.string,
  }).isRequired,
  step: PropTypes.node.isRequired,
};

export default WhySpecial;
